import { TeamColors, Player, TacticalPosition, Position } from "../domain/entities";
import { TACTICAL_POSITION_DETAILS, POSITION_MAP } from '../domain/constants';

// Re-export theme utilities for backward compatibility
export {
    parseColor,
    getLuminance,
    getContrast,
    rgbToHsl,
    hslToRgb,
    getContrastColor,
    getReadableAccentColor,
    setAccentColors,
    getAwayColor
} from './theme';

export const formatValue = (value: number): string => {
    const sign = value < 0 ? '-' : '';
    const absValue = Math.abs(value);

    if (absValue >= 1000000) {
        return `${sign}£${(absValue / 1000000).toFixed(1)}m`.replace('.0m', 'm');
    }
    if (absValue >= 1000) {
        return `${sign}£${Math.round(absValue / 1000)}k`;
    }
    return `${sign}£${Math.round(absValue).toLocaleString()}`;
};

export const calculateMatchPerformance = (player: Player): number => {
    const baseEffectiveSkill = player.skill + player.form;
    const fitnessModifier = 0.5 + (player.fitness / 200);
    const injuryModifier = player.injury?.type === 'Light' ? 0.8 : 1.0;
    return Math.round(baseEffectiveSkill * fitnessModifier * injuryModifier);
};

/**
 * Calculate role and foot flexibility modifiers for a player in a specific tactical position
 * Only applies when player's role or foot preference doesn't match the tactical position
 * Does NOT apply position-based penalties (all players must match their position)
 */
export const calculateRoleAndFootModifier = (player: Player, tacticalPosition: TacticalPosition): number => {
    if (!TACTICAL_POSITION_DETAILS[tacticalPosition]) {
        return 0;
    }

    const posDetails = TACTICAL_POSITION_DETAILS[tacticalPosition];
    let penalty = 0.0;

    // Ensure flexibility is initialized (defensive check for old saves)
    const flexibility = player.flexibility || { categoryFlexibility: 70, footFlexibility: 70 };
    const favoredPositionCategory = player.favoredPositionCategory || 'Any'; // Default to 'Any' if missing

    // Category mismatch penalty (Central vs Wide) - factored by categoryFlexibility
    if (favoredPositionCategory !== 'Any' && favoredPositionCategory !== posDetails.category) {
        const basePenalty = 0.06; // 6% base penalty for category mismatch
        const flexMod = (flexibility.categoryFlexibility - 40) / 100;
        penalty += basePenalty * (1 - flexMod);
    }

    // Foot mismatch penalty for non-wide positions - factored by footFlexibility
    const playerGeneralPos = player.position;
    const isWidePosition = posDetails.category === 'Wide';
    const favoredFoot = player.favoredFoot || 'Either'; // Default to 'Either' if missing
    if (!isWidePosition && favoredFoot !== 'Either' && posDetails.side !== 'Center' && favoredFoot.toLowerCase() !== posDetails.side.toLowerCase()) {
        const basePenalty = 0.03; // 3% base penalty for foot mismatch
        const flexMod = (flexibility.footFlexibility - 40) / 100;
        penalty += basePenalty * (1 - flexMod);
    }

    return Math.min(1.0, penalty); // Cap penalty at 100%
};

export const calculatePositionalPenalty = (player: Player, tacticalPosition: TacticalPosition): number => {
    // Legacy function - now just returns the role and foot modifier
    return calculateRoleAndFootModifier(player, tacticalPosition);
};

/**
 * Calculate expected match performance for a player in a specific tactical position
 * Includes all modifiers: skill, form, fitness, injury, AND positional penalty
 * This is the single source of truth for final performance calculation
 */
export const calculateExpectedPerformance = (player: Player, tacticalPosition: TacticalPosition | 'RES' = 'RES'): number => {
    // Base match performance (skill + form with fitness and injury modifiers)
    const basePerformance = calculateMatchPerformance(player);

    // Positional penalty (0 for reserves)
    const positionalPenalty = tacticalPosition !== 'RES' ? calculatePositionalPenalty(player, tacticalPosition) : 0;

    // Final performance is base performance reduced by positional penalty
    const finalPerformance = basePerformance * (1 - positionalPenalty);

    return Math.round(finalPerformance);
};

/**
 * Generate a flexibility assessment for a player based on their flexibility stats
 * Evaluates Central/Wide adaptability and weak foot ability
 */
export const generateFlexibilityAssessment = (player: Player): string => {
    // Ensure flexibility is initialized (defensive check for old saves)
    const flex = player.flexibility || { categoryFlexibility: 70, footFlexibility: 70 };

    // Average flexibility across dimensions
    const avgFlex = (flex.categoryFlexibility + flex.footFlexibility) / 2;

    const assessments: string[] = [];

    // Category flexibility (Central vs Wide)
    if (flex.categoryFlexibility >= 80) {
        assessments.push("equally comfortable central or wide");
    } else if (flex.categoryFlexibility >= 60) {
        assessments.push("adapts reasonably to central/wide roles");
    } else if (flex.categoryFlexibility < 50) {
        assessments.push("prefers their role (central or wide)");
    }

    // Foot flexibility
    if (flex.footFlexibility >= 75) {
        assessments.push("good on both feet");
    } else if (flex.footFlexibility >= 60) {
        assessments.push("decent weak foot");
    } else if (flex.footFlexibility < 50) {
        assessments.push("very one-footed");
    }

    // Overall assessment based on average
    let overallAssessment = "";
    if (avgFlex >= 75) {
        overallAssessment = "Highly adaptable player.";
    } else if (avgFlex >= 60) {
        overallAssessment = "Good tactical flexibility.";
    } else {
        overallAssessment = "Specialist player.";
    }

    return `${assessments.join(', ')}. ${overallAssessment}`;
};

/**
 * Get the appropriate Assistant Manager emoji based on game type
 * Returns a male face emoji for mens games, female face emoji for womens games
 */
export const getAssistantManagerEmoji = (gameType?: 'mens' | 'womens'): string => {
    return gameType === 'womens' ? '👩‍💼' : '👨‍💼';
};
